<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte/internal'
    import Fa from 'svelte-fa'
    import { faAngleLeft, faCircleInfo, faEye, faFloppyDisk, faTriangleExclamation, faUsers } from '@fortawesome/free-solid-svg-icons'
    import Zact from '../_common/z/zact.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import {
        AUDIENCE_TEMPLATES,
        TemplateCategory,
        TemplateCost,
        TemplateHealth,
        TemplateOrigin,
        buildCampaign,
        type AudienceTemplate,
        type TemplateService,
    } from './audience/audience-templates'
    import { AudienceRuleAct } from '../../modules/eventsApi/audienceRules/audiences.enum'

    const dispatch = createEventDispatcher()

    /**
     * Assistente de criação: monta público + automação numa tacada só.
     *
     * Existe porque para o usuário isso é UM valor, não dois. O schema concorda:
     * `AutomationGroup.audienceId` é obrigatório — automação não existe sem público.
     * A palavra "público" não aparece em lugar nenhum da tela; o usuário vê "quem vai receber".
     *
     * O que este componente NÃO faz: escrever a mensagem. Ele cria tudo e devolve o
     * automationGroupId para a tela de ações, que já sabe redigir. O `messageHint` do template
     * vai junto, como orientação.
     */

    let step: 1 | 2 = 1
    let show = false
    let saving = false

    let selected: AudienceTemplate = null
    let params: Record<string, any> = {}

    // pré-voo
    let previewTotal: number = null
    // maior contagem entre as regras de inclusão, antes das exclusões — é o que denuncia a exclusão
    // que anula a inclusão (mesmo evento, janelas sobrepostas): as regras acham gente, o total dá 0
    let previewIncluded: number = null
    let previewing = false
    let previewDirty = true

    // contexto da empresa, para o aviso de sobreposição
    let activeAudiences = 0
    let services: TemplateService[] = []

    const categoryLabels: Record<string, string> = {
        [TemplateCategory.lembrete]: 'Lembrar e confirmar',
        [TemplateCategory.relacionamento]: 'Relacionamento',
        [TemplateCategory.reativacao]: 'Trazer de volta',
        [TemplateCategory.vendas]: 'Vender mais',
        [TemplateCategory.retencao]: 'Reter',
        [TemplateCategory.base]: 'Comunicado geral',
    }

    // ordem de apresentação. NÃO ordenar por receita estimada: o número que temos está inflado
    // pela sobreposição de públicos (3 a 7 por pessoa) e não sobreviveu à checagem.
    const categoryOrder = [
        TemplateCategory.lembrete,
        TemplateCategory.reativacao,
        TemplateCategory.vendas,
        TemplateCategory.relacionamento,
        TemplateCategory.retencao,
        TemplateCategory.base,
    ]

    $: grouped = categoryOrder
        .map((cat) => ({
            category: cat,
            label: categoryLabels[cat],
            items: AUDIENCE_TEMPLATES.filter((el) => el.category === cat && el.health === TemplateHealth.ok),
        }))
        .filter((el) => el.items.length > 0)

    onMount(async () => {
        try {
            show = true
            const [audiences, svc] = await Promise.all([
                // pageSize do backend é 1000, então isto traz tudo
                window.api.events.audiences.getAll(),
                // segundo parâmetro é TTL de cache em segundos, como no resto do projeto
                window.api.pos.services.getAll(undefined, 600),
            ])
            activeAudiences = (audiences || []).filter((el) => el.status === 100).length
            services = (svc || []).filter((el) => el.status === 100).map((el) => ({ serviceId: el.serviceId, name: el.name }))
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    })

    function openTemplate(template: AudienceTemplate) {
        selected = template
        params = {}
        for (const p of template.params) {
            params[p.key] = p.default
        }
        previewTotal = null
        previewIncluded = null
        previewDirty = true
        step = 2
    }

    function backToList() {
        selected = null
        step = 1
    }

    function onParamChange() {
        previewDirty = true
    }

    // combinação que quebraria o público: trava conferir e criar até a pessoa corrigir
    $: paramError = selected?.validate ? selected.validate(params) : null

    function serviceOptions() {
        return services.map((el) => ({ value: el.serviceId, label: el.name }))
    }

    /** O Zinput de multiselect devolve ids; o template espera {serviceId, name}. */
    function toTemplateServices(ids: string[]): TemplateService[] {
        if (!Array.isArray(ids)) return []
        return services.filter((el) => ids.includes(el.serviceId))
    }

    function resolvedParams(): Record<string, any> {
        const out: Record<string, any> = {}
        for (const p of selected.params) {
            out[p.key] = p.type === 'services' ? toTemplateServices(params[p.key]) : params[p.key]
        }
        return out
    }

    /**
     * Pré-voo: quantas pessoas isso traria HOJE, sem criar nada.
     *
     * `POST /audiences/preview` aceita um audienceId que não existe no banco e devolve o
     * `totalDistinct` já COMBINADO (o motor aplica OR/AND/NOT antes de retornar). A tela de
     * regra atual mostra número por regra só porque manda uma regra por vez.
     *
     * Vale mais do que parece: erro de configuração quase não existe (98% das regras da base já
     * encontraram alguém). O que existe é falta de dado — empresa sem data de nascimento
     * preenchida fica com o público de aniversariantes vazio por meses sem saber.
     */
    async function runPreview() {
        if (!selected || paramError) return
        try {
            previewing = true
            const draft = buildCampaign(selected, resolvedParams())
            const result = await window.api.events.audiences.getPreview({
                audienceId: '00000000-0000-0000-0000-000000000000',
                audienceRules: draft.rules as any,
            })
            previewTotal = result?.totalDistinct ?? 0
            // evaluatedRules vem na mesma ordem das regras enviadas
            const evaluated = result?.evaluatedRules || []
            previewIncluded = Math.max(0, ...draft.rules.map((rule, i) => (rule.act !== AudienceRuleAct.Exclude ? evaluated[i]?.totalDistinct ?? 0 : 0)))
            previewDirty = false
        } catch (error) {
            window.error(error)
        } finally {
            previewing = false
        }
    }

    /**
     * Cria público, regras, grupo e ação.
     *
     * São quatro chamadas em DOIS backends, sem transação. Se quebrar depois do público, o
     * público fica órfão — provavelmente a origem dos 156 públicos dinâmicos sem regra que
     * existem hoje em produção. Por isso o rollback abaixo não é decorativo.
     */
    async function createCampaign() {
        if (!selected || saving || paramError) return
        const draft = buildCampaign(selected, resolvedParams())
        let audienceId: string = null
        try {
            saving = true

            // 1. público
            const audience = await window.api.events.audiences.create(draft.audience)
            audienceId = audience.audienceId

            // 2. regras, na ordem — a ordem de criação é a ordem de aplicação do motor
            const rulesApi = window.api.events.audienceRules(audienceId)
            for (const rule of draft.rules) {
                await rulesApi.create(rule)
            }

            // gera já, como a tela manual faz: sem isso o público ficava em stage 'new', fora do cron.
            // Falha aqui não desfaz nada — o público está válido e o cron gera na próxima volta.
            try {
                await window.api.events.audiences.generateAudience(audienceId)
            } catch (generateError) {
                console.log('assistente_generate_falhou', generateError)
            }

            // 3. grupo de automação
            const group = await window.api.automations.automationGroups.create({
                ...draft.group,
                audienceId,
            })

            // 4. ação
            await window.api.automations.automations(group.automationGroupId).create({
                ...draft.automation,
                automationGroupId: group.automationGroupId,
            })

            window.api.users.statistics.tick('automation.assistant.create')
            dispatch('created', {
                automationGroupId: group.automationGroupId,
                audienceId,
                messageHint: draft.messageHint,
            })
        } catch (error) {
            // rollback do que já entrou, para não deixar público órfão
            if (audienceId) {
                try {
                    await window.api.events.audiences.remove(audienceId)
                } catch (rollbackError) {
                    console.log('assistente_rollback_falhou', rollbackError)
                }
            }
            window.error(error)
        } finally {
            saving = false
        }
    }

    function close() {
        dispatch('close')
    }
</script>

{#if step === 1}
    <Zact>
        <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={close} />
        <div class="spacer" />
    </Zact>

    <!-- Um Zframe só para o catálogo inteiro, com as categorias como título interno: seis
         molduras empilhadas deixavam a tela esparsa e cheia de cabeçalho repetido. -->
    <Zframe class="h-space" title="O que você quer fazer?">
        {#if activeAudiences >= 5}
            <!-- Aviso de sobreposição. Na base de produção a mesma pessoa entra em 3 a 7 públicos
                 por trimestre, e o cooldown de repetição é POR AUTOMAÇÃO: nada coordena as
                 automações entre si. Cada automação nova é mais uma mensagem possível. -->
            <div class="aviso">
                <Fa icon={faTriangleExclamation} />
                <div>
                    Você já tem <b>{activeAudiences} automações</b> enviando mensagem. A mesma pessoa costuma cair em várias delas, e cada uma envia a sua — vale
                    conferir se ela não vai receber demais.
                </div>
            </div>
        {/if}

        {#each grouped as group, i}
            <div class="grupo" class:primeiro={i === 0}>{group.label}</div>
            <div class="cards">
                {#each group.items as template}
                    <button class="card" on:click={() => openTemplate(template)}>
                        <div class="card-head">
                            <span class="card-title">{template.title}</span>
                            <!-- {#if template.origin === TemplateOrigin.proposed}
                                <span class="tag novo">novo</span>
                            {/if} -->
                            <!-- {#if template.cost === TemplateCost.high}
                                <span class="tag pesado">pesado</span>
                            {/if} -->
                        </div>
                        <div class="card-desc">{template.description}</div>
                    </button>
                {/each}
            </div>
        {/each}
    </Zframe>
{/if}

{#if step === 2 && selected}
    <Zact>
        <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={backToList} />
        <div class="spacer" />
        <Zbutton color="secondary" icon={faEye} label="Ver quantas pessoas" loading={previewing} disabled={previewing || saving || !!paramError} on:click={runPreview} />
        <Zbutton color="primary" icon={faFloppyDisk} label="Criar e escrever a mensagem" loading={saving} disabled={saving || !!paramError} on:click={createCampaign} />
    </Zact>

    <Zframe class="h-space" title={selected.title} headline={selected.description}>
        <!-- Para que serve. É o campo que abre a cabeça de quem entende o filtro mas não
             enxerga a oportunidade. -->
        <div class="usos">
            <div class="usos-titulo"><Fa icon={faCircleInfo} /> O que dá para fazer com isso</div>
            <ul>
                {#each selected.useCases as uso}
                    <li>{uso}</li>
                {/each}
            </ul>
        </div>

        {#if selected.caveat}
            <div class="aviso">
                <Fa icon={faTriangleExclamation} />
                <div>{selected.caveat}</div>
            </div>
        {/if}

        {#if selected.params.length > 0}
            <ZContainer gapRow="8px" gapCol="8px">
                {#each selected.params as p}
                    {#if p.type === 'services'}
                        <Zinput
                            size={6}
                            sizeM={12}
                            type="multiselect"
                            label={p.label}
                            help={p.help}
                            options={serviceOptions()}
                            bind:value={params[p.key]}
                            on:change={onParamChange}
                        />
                    {:else if p.options?.length}
                        <Zinput
                            size={4}
                            sizeM={12}
                            type="select"
                            label={p.label}
                            help={p.help}
                            options={p.options}
                            bind:value={params[p.key]}
                            on:change={onParamChange}
                        />
                    {:else if p.type === 'date'}
                        <Zinput size={3} sizeM={12} type="date" label={p.label} help={p.help} bind:value={params[p.key]} on:change={onParamChange} />
                    {:else if p.type === 'toggle'}
                        <Zinput size={4} sizeM={12} type="toggle" label={p.label} help={p.help} bind:value={params[p.key]} on:change={onParamChange} />
                    {:else}
                        <Zinput size={3} sizeM={12} type="number" label={p.label} help={p.help} bind:value={params[p.key]} on:change={onParamChange} />
                    {/if}
                {/each}
            </ZContainer>
        {/if}

        {#if paramError}
            <div class="aviso h-space">
                <Fa icon={faTriangleExclamation} />
                <div>{paramError}</div>
            </div>
        {/if}

        <div class="preview">
            {#if previewTotal === null}
                <div class="preview-vazio">
                    <Fa icon={faUsers} /> Clique em <b>Ver quantas pessoas</b> para conferir antes de criar.
                </div>
            {:else}
                <div class="preview-num" class:zero={previewTotal === 0}>{previewTotal}</div>
                <div class="preview-label">
                    {previewTotal === 1 ? 'pessoa se encaixa hoje' : 'pessoas se encaixam hoje'}
                    {#if previewDirty}<span class="desatualizado">— você mudou algo, confira de novo</span>{/if}
                </div>
                {#if previewTotal === 0 && previewIncluded > 0}
                    <div class="preview-ajuda">
                        A busca encontra {previewIncluded}
                        {previewIncluded === 1 ? 'pessoa' : 'pessoas'}, mas a parte "exceto quem…" tira todas. Quase sempre é o mesmo serviço escolhido como ponto de
                        partida e como exclusão — revise as escolhas acima.
                    </div>
                {:else if previewTotal === 0}
                    <div class="preview-ajuda">
                        Ninguém se encaixa agora. Isso costuma ser falta de informação no cadastro, não erro de configuração — vale conferir se os dados que
                        esta regra usa estão preenchidos.
                    </div>
                {/if}
            {/if}
        </div>

        <div class="rodape">Depois de criar, as primeiras pessoas aparecem em alguns minutos — não é instantâneo.</div>
    </Zframe>
{/if}

<LoadSpinner {show} />

<style>
    .grupo {
        font-size: 0.78rem;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        opacity: 0.55;
        margin: 20px 0 8px;
    }
    .grupo.primeiro {
        margin-top: 4px;
    }

    .cards {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 8px;
        align-items: stretch;
    }
    .card {
        /* `button` não herda cor do pai: sem isto o texto sai preto sobre o cartão escuro e
           some no tema padrão do app. Era o motivo de os cartões parecerem apagados. */
        color: inherit;
        font: inherit;
        display: flex;
        flex-direction: column;
        gap: 5px;
        padding: 11px 12px;
        border: 1px solid #d5d8dd;
        border-radius: 8px;
        background: #ffffff;
        text-align: left;
        cursor: pointer;
        transition:
            border-color 0.15s,
            background 0.15s;
    }
    .card:hover {
        border-color: #2f8f63;
        background: #f6fbf8;
    }
    :global(.theme_dark) .card {
        background: #202020;
        border-color: #3a3a3a;
    }
    :global(.theme_dark) .card:hover {
        background: #262b27;
        border-color: #2f8f63;
    }
    .card-head {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
    }
    .card-title {
        font-weight: 600;
        line-height: 1.25;
    }
    .card-desc {
        font-size: 0.82rem;
        opacity: 0.7;
        line-height: 1.4;
    }
    .tag {
        font-size: 0.62rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        padding: 1px 6px;
        border-radius: 10px;
        font-weight: 600;
    }
    .tag.novo {
        background: #d8efe2;
        color: #1d6b45;
    }
    .tag.pesado {
        background: #f6e5c8;
        color: #7a5316;
    }

    .aviso {
        display: flex;
        gap: 8px;
        align-items: flex-start;
        padding: 10px 12px;
        border-radius: 8px;
        background: #fdf3da;
        color: #6b4e12;
        font-size: 0.88rem;
        line-height: 1.4;
        margin-bottom: 12px;
    }
    :global(.theme_dark) .aviso {
        background: #3b3221;
        color: #e8d3a2;
    }

    .usos {
        padding: 10px 12px;
        border-radius: 8px;
        background: #eef2f8;
        margin-bottom: 12px;
    }
    :global(.theme_dark) .usos {
        background: #262b33;
    }
    .usos-titulo {
        font-weight: 600;
        font-size: 0.88rem;
        margin-bottom: 6px;
    }
    .usos ul {
        margin: 0;
        padding-left: 18px;
    }
    .usos li {
        font-size: 0.86rem;
        line-height: 1.5;
        opacity: 0.85;
    }

    .preview {
        margin-top: 14px;
        padding: 14px;
        border-radius: 8px;
        border: 1px dashed #c8ccd3;
        text-align: center;
    }
    :global(.theme_dark) .preview {
        border-color: #3f444c;
    }
    .preview-vazio {
        font-size: 0.9rem;
        opacity: 0.7;
    }
    .preview-num {
        font-size: 2.1rem;
        font-weight: 700;
        line-height: 1.1;
        color: #2f8f63;
    }
    .preview-num.zero {
        color: #a8672a;
    }
    .preview-label {
        font-size: 0.88rem;
        opacity: 0.75;
    }
    .desatualizado {
        opacity: 0.8;
        font-style: italic;
    }
    .preview-ajuda {
        margin-top: 8px;
        font-size: 0.84rem;
        line-height: 1.45;
        opacity: 0.8;
    }
    .rodape {
        margin-top: 12px;
        font-size: 0.8rem;
        opacity: 0.6;
    }
</style>
